import { Message } from './api';
import { getFullUrl } from '../config/api.config';

interface StoredMessage extends Message {
  storedAt: number;
  deviceId: string;
  imageBlob?: string; // base64 data URL for single images (backward compatibility)
  fileBlobs?: Record<string, string>; // Map of file URLs to base64 data URLs for files array (stored as object for JSON)
}

class LocalStorageService {
  private dbName = 'BufferAppDB';
  private dbVersion = 1;
  private storeName = 'messages';
  private db: IDBDatabase | null = null;

  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => {
        reject(new Error('Failed to open IndexedDB'));
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        
        // Create messages store if it doesn't exist
        if (!db.objectStoreNames.contains(this.storeName)) {
          const store = db.createObjectStore(this.storeName, { keyPath: 'contentId' });
          store.createIndex('deviceId', 'deviceId', { unique: false });
          store.createIndex('timestamp', 'timestamp', { unique: false });
          store.createIndex('storedAt', 'storedAt', { unique: false });
        }
      };
    });
  }

  async saveMessage(deviceId: string, message: Message): Promise<void> {
    // Wrapper for storing a single message
    return this.storeMessages(deviceId, [message]);
  }

  async storeMessages(deviceId: string, messages: Message[]): Promise<void> {
    if (!this.db) {
      await this.init();
    }

    // First, process all messages and download images
    const processedMessages: StoredMessage[] = [];
    
    for (const message of messages) {
      const storedMessage: StoredMessage = {
        ...message,
        storedAt: Date.now(),
        deviceId
      };

      // Download and store image if it's an image message (backward compatibility)
      if (message.type === 'image' && message.imageUrl) {
        try {
          console.log('Downloading image for message:', message.contentId, 'URL:', message.imageUrl);
          const imageBlob = await this.downloadAndStoreImage(message.imageUrl);
          storedMessage.imageBlob = imageBlob;
          // Remove the server URL since we now have local data
          storedMessage.imageUrl = undefined;
          console.log('Successfully downloaded and stored image for message:', message.contentId, 'Base64 size:', imageBlob.length);
        } catch (err) {
          console.error('Failed to download image for message:', message.contentId, 'URL:', message.imageUrl, err);
          // Keep the original imageUrl as fallback - don't store corrupted data
          console.log('Keeping original server URL as fallback');
        }
      }

      // Download and store files if it's a files message
      if (message.type === 'files' && message.files && message.files.length > 0) {
        try {
          console.log('Downloading files for message:', message.contentId, 'Files count:', message.files.length);
          const fileBlobs = new Map<string, string>();
          
          for (const file of message.files) {
            try {
              // Skip if already a data URL (already local)
              if (file.url.startsWith('data:')) {
                fileBlobs.set(file.url, file.url);
                continue;
              }

              const fileBlob = await this.downloadAndStoreFile(file.url, file.type);
              fileBlobs.set(file.url, fileBlob);
              console.log('Successfully downloaded and stored file:', file.name || file.url, 'Base64 size:', fileBlob.length);
            } catch (err) {
              console.error('Failed to download file for message:', message.contentId, 'URL:', file.url, err);
              // Keep the original URL as fallback
            }
          }

          if (fileBlobs.size > 0) {
            // Store file blobs map (convert to object for JSON serialization)
            // Keep original URLs in files array, we'll map them when retrieving
            storedMessage.fileBlobs = Object.fromEntries(fileBlobs);
            // Note: We keep the original URLs in files array and use fileBlobs to map them when retrieving
          }
        } catch (err) {
          console.error('Failed to download files for message:', message.contentId, err);
        }
      }
      
      processedMessages.push(storedMessage);
    }

    // Now store all processed messages in IndexedDB
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      
      const storePromises = processedMessages.map(message => {
        return new Promise<void>((storeResolve, storeReject) => {
          const request = store.put(message);
          request.onsuccess = () => storeResolve();
          request.onerror = () => storeReject(new Error('Failed to store message'));
        });
      });

      Promise.all(storePromises)
        .then(() => resolve())
        .catch(reject);
    });
  }

  private async downloadAndStoreImage(imageUrl: string): Promise<string> {
    return this.downloadAndStoreFile(imageUrl, 'image/jpeg');
  }

  private async downloadAndStoreFile(fileUrl: string, fileType?: string): Promise<string> {
    try {
      // Skip if already a data URL
      if (fileUrl.startsWith('data:')) {
        return fileUrl;
      }

      // Convert relative URL to absolute URL using centralized config
      const fullUrl = getFullUrl(fileUrl);
      
      console.log('Downloading file from:', fullUrl);
      
      const response = await fetch(fullUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.status} ${response.statusText}`);
      }
      
      const blob = await response.blob();
      
      // Convert blob to base64 data URL
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (err) {
      console.error('Error downloading file from URL:', fileUrl, err);
      throw err;
    }
  }

  async getMessages(deviceId: string): Promise<Message[]> {
    if (!this.db) {
      await this.init();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const index = store.index('deviceId');
      const request = index.getAll(deviceId);

      request.onsuccess = () => {
        const messages = request.result.map((stored: StoredMessage) => {
          // Remove local storage specific fields and restore image/file data
          const { storedAt, deviceId: _, imageBlob, fileBlobs, ...message } = stored;
          
          // If we have locally stored image data, use it instead of the URL (backward compatibility)
          if (message.type === 'image' && imageBlob) {
            console.log('Using local image data for message:', message.contentId, 'Data size:', imageBlob.length);
            message.imageUrl = imageBlob; // Use the base64 data URL
          } else if (message.type === 'image') {
            console.log('No local image data found, using server URL for message:', message.contentId, 'URL:', message.imageUrl);
          }

          // If we have locally stored file data, use it instead of server URLs
          if (message.type === 'files' && message.files) {
            message.files = message.files.map(file => {
              // If already a data URL, use it directly
              if (file.url.startsWith('data:')) {
                return file;
              }
              
              // Check if we have a local blob for this file URL
              if (fileBlobs && fileBlobs[file.url]) {
                return {
                  ...file,
                  url: fileBlobs[file.url] // Use the base64 data URL
                };
              }
              
              // Keep original URL if no local blob found
              return file;
            });
            
            if (fileBlobs) {
              console.log('Using local file data for message:', message.contentId, 'Files count:', message.files.length);
            }
          }
          
          return message;
        });
        
        // Sort by timestamp (newest first)
        messages.sort((a, b) => b.timestamp - a.timestamp);
        resolve(messages);
      };

      request.onerror = () => {
        reject(new Error('Failed to retrieve messages'));
      };
    });
  }

  // Mark all messages for a device as read
  async markAllMessagesAsRead(deviceId: string): Promise<void> {
    if (!this.db) {
      await this.init();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const index = store.index('deviceId');
      const request = index.getAll(deviceId);

      request.onsuccess = () => {
        const updatePromises = request.result.map((stored: StoredMessage) => {
          return new Promise<void>((updateResolve, updateReject) => {
            if (!stored.isRead) {
              stored.isRead = true;
              stored.readAt = new Date().toISOString();
              const updateRequest = store.put(stored);
              updateRequest.onsuccess = () => updateResolve();
              updateRequest.onerror = () => updateReject(new Error('Failed to update message'));
            } else {
              updateResolve();
            }
          });
        });

        Promise.all(updatePromises)
          .then(() => resolve())
          .catch(reject);
      };

      request.onerror = () => {
        reject(new Error('Failed to mark messages as read'));
      };
    });
  }

  async deleteMessage(messageId: string): Promise<void> {
    if (!this.db) {
      await this.init();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.delete(messageId);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error('Failed to delete message'));
    });
  }

  async clearMessages(deviceId: string): Promise<void> {
    if (!this.db) {
      await this.init();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const index = store.index('deviceId');
      const request = index.getAllKeys(deviceId);

      request.onsuccess = () => {
        const deletePromises = request.result.map(key => {
          return new Promise<void>((deleteResolve, deleteReject) => {
            const deleteRequest = store.delete(key);
            deleteRequest.onsuccess = () => deleteResolve();
            deleteRequest.onerror = () => deleteReject(new Error('Failed to delete message'));
          });
        });

        Promise.all(deletePromises)
          .then(() => resolve())
          .catch(reject);
      };

      request.onerror = () => {
        reject(new Error('Failed to clear messages'));
      };
    });
  }

  async clearAllMessages(): Promise<void> {
    if (!this.db) {
      await this.init();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.clear();

      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error('Failed to clear all messages'));
    });
  }

  async getMessageCount(deviceId: string): Promise<number> {
    if (!this.db) {
      await this.init();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const index = store.index('deviceId');
      const request = index.count(deviceId);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(new Error('Failed to count messages'));
    });
  }

  async getStorageSize(): Promise<{ messageCount: number; estimatedSize: string }> {
    if (!this.db) {
      await this.init();
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const request = store.count();

      request.onsuccess = () => {
        const messageCount = request.result;
        
        // Estimate size (rough calculation)
        // Base64 images are ~33% larger than original
        // Average message size: ~1KB for text, ~100KB for images
        const estimatedSizeInBytes = messageCount * 50000; // Conservative estimate
        const estimatedSize = this.formatBytes(estimatedSizeInBytes);
        
        resolve({ messageCount, estimatedSize });
      };

      request.onerror = () => {
        reject(new Error('Failed to get storage size'));
      };
    });
  }

  private formatBytes(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  // Cleanup old messages (older than 30 days)
  async cleanupOldMessages(): Promise<void> {
    if (!this.db) {
      await this.init();
    }

    const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000);

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const index = store.index('storedAt');
      const range = IDBKeyRange.upperBound(thirtyDaysAgo);
      const request = index.getAllKeys(range);

      request.onsuccess = () => {
        const deletePromises = request.result.map(key => {
          return new Promise<void>((deleteResolve, deleteReject) => {
            const deleteRequest = store.delete(key);
            deleteRequest.onsuccess = () => deleteResolve();
            deleteRequest.onerror = () => deleteReject(new Error('Failed to delete old message'));
          });
        });

        Promise.all(deletePromises)
          .then(() => resolve())
          .catch(reject);
      };

      request.onerror = () => {
        reject(new Error('Failed to cleanup old messages'));
      };
    });
  }
}

export const localStorageService = new LocalStorageService();
